import { Token } from "@siteimprove/alfa-css";
import { None, Option } from "@siteimprove/alfa-option";
import { Parser } from "@siteimprove/alfa-parser";
import { Condition } from "../condition/index.js";
import { Media } from "./feature/index.js";
import { Modifier } from "./modifier.js";
import { Type } from "./type.js";
const { delimited, either, end, left, map, option, pair, right } = Parser;
/**
 * {@link https://drafts.csswg.org/mediaqueries-5/#media-query}
 *
 * @remarks
 * Media query can contain both a modifier, type and media feature.
 *
 * @public
 */
export class Query {
    static of(modifier, type, condition) {
        return new Query(modifier, type, condition);
    }
    _modifier;
    _type;
    _condition;
    constructor(modifier, type, condition) {
        this._modifier = modifier;
        this._type = type;
        this._condition = condition;
    }
    get modifier() {
        return this._modifier;
    }
    get type() {
        return this._type;
    }
    get condition() {
        return this._condition;
    }
    matches(device) {
        const negated = this._modifier.some((modifier) => modifier === Modifier.Not);
        const type = this._type.every((type) => type.matches(device));
        const condition = this.condition.every((condition) => condition.matches(device));
        return negated !== (type && condition);
    }
    *iterator() {
        yield* this._condition;
    }
    [Symbol.iterator]() {
        return this.iterator();
    }
    equals(value) {
        return (value instanceof Query &&
            value._modifier.equals(this._modifier) &&
            value._type.equals(this._type) &&
            value._condition.equals(this._condition));
    }
    toJSON() {
        return {
            modifier: this._modifier.getOr(null),
            type: this._type.map((type) => type.toJSON()).getOr(null),
            condition: this._condition
                .map((condition) => condition.toJSON())
                .getOr(null),
        };
    }
    toString() {
        const modifier = this._modifier.getOr("");
        const type = this._type
            .map((type) => (modifier === "" ? `${type}` : `${modifier} ${type}`))
            .getOr("");
        return this._condition
            .map((condition) => type === "" ? `${condition}` : `${type} and ${condition}`)
            .getOr(type);
    }
}
/**
 * @public
 */
(function (Query) {
    function isQuery(value) {
        return value instanceof Query;
    }
    Query.isQuery = isQuery;
    /**
     * @internal
     */
    Query.notAll = Query.of(Option.of(Modifier.Not), Option.of(Type.of("all")), None);
    /**
     * {@link https://drafts.csswg.org/mediaqueries-5/#typedef-media-query}
     */
    Query.parse = left(either(map(Condition.parse(Media.parse), (condition) => Query.of(None, None, Option.of(condition))), map(pair(pair(option(delimited(option(Token.parseWhitespace), Modifier.parse)), Type.parse), option(right(delimited(option(Token.parseWhitespace), Token.parseIdent("and")), Condition.parseWithoutOr(Media.parse)))), ([[modifier, type], condition]) => Query.of(modifier, Option.of(type), condition))), end(() => `Unexpected token`));
})(Query || (Query = {}));
//# sourceMappingURL=query.js.map